import { Injectable, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthHttpService } from './auth-http.service';
import { LoggerService } from './logger.service';
import { InactivityService } from './inactivity.service';
import { ActiveActivationService } from './active-activation.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class TokenExpirationService {
  private checkInterval?: number;
  private countdownInterval?: number;
  showExpirationWarning = signal(false);
  remainingSeconds = signal(0);

  private authHttpService = inject(AuthHttpService);
  private logger = inject(LoggerService);
  private inactivityService = inject(InactivityService);
  private activeActivation = inject(ActiveActivationService);

  constructor(private router: Router) {}

  startMonitoring(): void {
    this.stopMonitoring();
    this.checkInterval = window.setInterval(() => this.checkTokenExpiration(), environment.sessionCheckIntervalMs);
    this.checkTokenExpiration(); // Verificación inmediata
  }

  stopMonitoring(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = undefined;
    }
    this.stopCountdown();
  }

  private stopCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = undefined;
    }
  }

  private checkTokenExpiration(): void {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      this.logger.log('[TokenExpiration] checkTokenExpiration — sin token en localStorage');
      return;
    }

    const expirationTime = this.getTokenExpiration(token);
    if (!expirationTime) {
      this.logger.warn('[TokenExpiration] checkTokenExpiration — no se pudo leer exp del token');
      return;
    }

    const timeUntilExpiration = expirationTime - Date.now();
    const secondsRemaining = Math.ceil(timeUntilExpiration / 1000);
    this.logger.log(`[TokenExpiration] Tiempo restante: ${secondsRemaining}s | warningThreshold: ${environment.sessionWarningTimeMs / 1000}s`);

    if (timeUntilExpiration <= environment.sessionWarningTimeMs && timeUntilExpiration > 0) {
      const userActive = this.inactivityService.isUserActive();
      this.logger.log(`[TokenExpiration] Dentro de ventana de advertencia — usuarioActivo: ${userActive} | showWarning actual: ${this.showExpirationWarning()}`);

      if (userActive) {
        this.logger.log('[TokenExpiration] Usuario activo → auto-refresh del token');
        this.autoRefreshToken();
      } else {
        this.logger.log('[TokenExpiration] Usuario inactivo → mostrando modal de advertencia');
        this.showExpirationWarning.set(true);
        this.stopMonitoring();
        this.startCountdown(secondsRemaining);
      }
    } else if (timeUntilExpiration <= 0) {
      this.logger.warn('[TokenExpiration] Token ya expirado → cerrando sesión');
      this.handleSessionExpired();
    } else {
      this.logger.log(`[TokenExpiration] Fuera de ventana de advertencia — faltan ${secondsRemaining}s`);
    }
  }

  private getTokenExpiration(token: string): number | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp ? payload.exp * 1000 : null;
    } catch {
      return null;
    }
  }

  private startCountdown(seconds: number): void {
    this.remainingSeconds.set(seconds);
    this.logger.log(`[TokenExpiration] Countdown iniciado — ${seconds}s`);
    this.countdownInterval = window.setInterval(() => {
      const remaining = this.remainingSeconds() - 1;
      this.remainingSeconds.set(remaining);
      if (remaining <= 0) {
        this.logger.warn('[TokenExpiration] Countdown llegó a 0 → cerrando sesión');
        this.closeSession();
      }
    }, 1000);
  }

  continueSession(): void {
    this.stopCountdown();
    this.refreshToken();
  }

  private autoRefreshToken(): void {
    this.refreshToken();
  }

  private refreshToken(): void {
    const sessionId = localStorage.getItem('sessionId');
    const refreshToken = localStorage.getItem('refreshToken');

    if (!sessionId || !refreshToken) {
      this.logger.warn('[TokenExpiration] refreshToken — faltan sessionId o refreshToken → expirando sesión');
      this.handleSessionExpired();
      return;
    }

    this.logger.log('[TokenExpiration] Llamando a refreshToken...');
    this.authHttpService.refreshToken(sessionId, refreshToken).subscribe({
      next: (response) => {
        if (response?.success && response?.data?.accessToken && response?.data?.refreshToken) {
          localStorage.setItem('accessToken', response.data.accessToken);
          localStorage.setItem('refreshToken', response.data.refreshToken);
          this.logger.log('[TokenExpiration] Token refrescado exitosamente — reanudando monitoreo');
          this.showExpirationWarning.set(false);
          this.startMonitoring();
        } else {
          this.logger.warn('[TokenExpiration] refreshToken — respuesta inválida → expirando sesión');
          this.handleSessionExpired();
        }
      },
      error: (err) => {
        this.logger.error('[TokenExpiration] refreshToken ERROR:', err);
        this.handleSessionExpired();
      }
    });
  }

  closeSession(): void {
    this.showExpirationWarning.set(false);
    this.handleSessionExpired();
  }

  private handleSessionExpired(): void {
    this.stopMonitoring();

    // Si hay una activación prepago en curso, abandonarla antes del logout
    // para no dejar activaciones huérfanas en el backend.
    this.activeActivation.abandonIfActive().then(() => {
      const sessionId = localStorage.getItem('sessionId');
      this.authHttpService.logout(sessionId!).subscribe({
        next: (response) => {
          if (response.success) {
            localStorage.clear();
            this.router.navigate(['/login']);
          }
        }
      });
    });
  }
}
